import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { PushPrompt } from '../components/PushPrompt';
import { ThemeToggle } from '../components/ThemeToggle';
import { Alert, Icon, StatusPill } from '../components/ui';
import { useAuth, type Membership } from '../lib/auth';
import { datePt, subscriptionLabel, trialDaysLeft } from '../lib/format';
import { applyTheme, currentTheme } from '../lib/theme';
import { tr } from '../lib/i18n';

const OPERATIONAL = new Set(['trialing', 'active']);

export const PANEL_NAV = [
  { to: '/painel', label: 'Visão geral', icon: 'chart', end: true },
  { to: '/painel/agenda', label: 'Agenda', icon: 'calendar' },
  { to: '/painel/agendamentos', label: 'Agendamentos', icon: 'clock' },
  { to: '/painel/servicos', label: 'Serviços', icon: 'scissors' },
  { to: '/painel/clientes', label: 'Clientes', icon: 'user' },
  { to: '/painel/perfil', label: 'Perfil público', icon: 'star' },
  { to: '/painel/relatorios', label: 'Relatórios', icon: 'chart' },
  { to: '/painel/configuracoes', label: 'Configurações', icon: 'settings' },
  { to: '/painel/suporte', label: 'Suporte', icon: 'info' },
  { to: '/painel/plano', label: 'Meu plano', icon: 'settings' }
];

export function subscriptionTone(sub: Membership['subscription']): 'positive' | 'warning' | 'danger' | 'neutral' {
  if (!sub) return 'danger';
  if (sub.status === 'active') return 'positive';
  if (sub.status === 'trialing') return 'warning';
  if (sub.status === 'suspended') return 'danger';
  return 'neutral';
}

export function PanelLayout({ children }: { children: ReactNode }): JSX.Element {
  const { active, memberships, selectBusiness, logout, isAdmin } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const businessTheme = active?.businessTheme ?? null;
  useEffect(() => {
    if (!businessTheme) return;
    const previous = currentTheme();
    applyTheme(businessTheme, false);
    return () => {
      applyTheme(previous, false);
    };
  }, [businessTheme]);

  const sub = active?.subscription ?? null;
  const operational = OPERATIONAL.has(sub?.status ?? '');
  const daysLeft = trialDaysLeft(sub?.trialEndsAt);
  // Na tela inicial sem assinatura vigente o próprio conteúdo já é a escolha do plano.
  const atBillingGate = Boolean(active) && !operational && location.pathname === '/painel';
  // Sem licença vigente só a ativação, o plano e o suporte continuam fazendo sentido.
  const navItems = operational || !active
    ? PANEL_NAV
    : PANEL_NAV.filter((item) => ['/painel', '/painel/plano', '/painel/suporte'].includes(item.to));

  const banner = useMemo(() => {
    if (!active) return null;
    if (!sub) {
      return (
        <Alert tone="warning" title={tr("Nenhuma licença ativa")}>
          {tr("Use um código de licença ou contrate um plano em")} <Link className="font-semibold underline" to="/painel/plano">{tr("Meu plano")}</Link> {tr("para liberar os recursos operacionais.")}
        </Alert>
      );
    }
    if (sub.status === 'trialing') {
      return (
        <Alert tone="info" title={`Teste gratuito: ${daysLeft} ${daysLeft === 1 ? 'dia restante' : 'dias restantes'}`}>
          {tr("Encerra em")} {datePt(sub.trialEndsAt)}{tr(". Ao final, é necessária uma assinatura paga para continuar usando a agenda.")}
        </Alert>
      );
    }
    if (sub.status === 'expired') {
      return (
        <Alert tone="danger" title={tr("Teste gratuito encerrado")}>
          {tr("Seu teste gratuito de sete dias terminou. Ative um código de licença ou escolha um plano na")} <Link className="font-semibold underline" to="/painel">{tr("tela inicial")}</Link> {tr("para continuar utilizando sua agenda e gerenciando seu negócio.")}
        </Alert>
      );
    }
    if (sub.status === 'past_due') {
      return (
        <Alert tone="danger" title={tr("Pagamento pendente")}>
          {tr("Sua assinatura está com cobrança pendente. Regularize em")} <Link className="font-semibold underline" to="/painel/plano">{tr("Meu plano")}</Link>.
        </Alert>
      );
    }
    if (sub.status === 'suspended') {
      return <Alert tone="danger" title={tr("Conta suspensa")}>{tr("O acesso operacional foi suspenso pela AgendaPro. Fale com o suporte.")}</Alert>;
    }
    return null;
  }, [active, sub, daysLeft]);

  return (
    <div className="flex min-h-screen flex-col bg-bg">
      <header className="sticky top-0 z-40 bg-chrome text-chrome-ink shadow-md">
        <div className="mx-auto flex h-[68px] w-full max-w-content items-center gap-3 px-4">
          <button
            type="button"
            className="rounded-full p-2 hover:bg-white/10 lg:hidden"
            aria-label={menuOpen ? tr('Fechar menu') : tr('Abrir menu')}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <Icon name={menuOpen ? 'close' : 'menu'} size={22} />
          </button>
          <Link to="/painel" className="flex min-w-0 items-center gap-2.5">
            {active?.businessLogo ? (
              <>
                <img
                  src={active.businessLogo}
                  alt={`Logo de ${active.businessName}`}
                  className="h-9 w-auto max-w-[150px] rounded-xl object-contain"
                />
                <span className="hidden truncate font-display text-lg font-semibold sm:inline">{active.businessName}</span>
              </>
            ) : (
              <Logo variant="light" size={38} wordSize={20} />
            )}
          </Link>
          <div className="ml-auto flex items-center gap-2">
            {sub ? (
              <span className="hidden sm:inline-flex">
                <StatusPill tone={subscriptionTone(sub)}>{subscriptionLabel(sub.status)}</StatusPill>
              </span>
            ) : null}
            <Link to="/" className="hidden rounded-full px-4 py-2 text-sm font-medium hover:bg-white/10 sm:inline-block">
              {tr("Ver meu site")}
            </Link>
            <button
              type="button"
              onClick={() => void logout().then(() => navigate('/'))}
              className="rounded-full px-4 py-2 text-sm font-medium hover:bg-white/10"
            >
              {tr("Sair")}
            </button>
            <ThemeToggle tone="dark" />
          </div>
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-content flex-1 flex-col gap-6 px-4 py-6 lg:flex-row lg:gap-8 lg:py-8">
        <aside
          className={`${menuOpen ? 'block' : 'hidden'} w-full shrink-0 lg:sticky lg:top-[92px] lg:block lg:w-60 lg:self-start`}
          aria-label={tr("Menu do painel")}
        >
          {isAdmin ? (
            <Link
              to="/admin"
              className="mb-3 flex items-center gap-3 rounded-2xl border border-sand bg-chrome-strong px-3 py-2.5 text-sm font-semibold text-chrome-ink hover:bg-chrome"
            >
              <Icon name="shield" size={17} /> {tr("Administração da plataforma")}
            </Link>
          ) : null}
          {memberships.length > 1 ? (
            <label className="mb-3 block">
              <span className="nl-label">{tr("Estabelecimento")}</span>
              <select
                className="nl-input"
                value={active?.businessId ?? ''}
                onChange={(e) => selectBusiness(e.target.value)}
              >
                {memberships.map((m) => (
                  <option key={m.businessId} value={m.businessId}>
                    {m.businessName}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <nav className="nl-card space-y-1 overflow-hidden p-2">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-full px-4 py-3 text-sm font-semibold transition-all ${
                    isActive
                      ? 'bg-accent text-on-accent shadow-[0_10px_20px_-14px_var(--nl-accent)]'
                      : 'text-muted hover:bg-surface hover:text-graphite'
                  }`
                }
              >
                <Icon name={item.icon} size={18} />
                {tr(item.label)}
              </NavLink>
            ))}
          </nav>
        </aside>

        <div className="min-w-0 flex-1">
          {operational || !active || atBillingGate ? null : (
            <div className="mb-4">
              <Alert tone="danger" title={tr("Acesso operacional bloqueado")}>
                {tr("As telas de gestão continuam respondendo com pedido de assinatura. Contrate um plano na")} <Link className="font-semibold underline" to="/painel">{tr("tela inicial")}</Link>.
              </Alert>
            </div>
          )}
          {banner && !atBillingGate ? <div className="mb-4">{banner}</div> : null}
          {active && operational && !atBillingGate ? <PushPrompt businessId={active.businessId} /> : null}
          {children}
        </div>
      </div>
    </div>
  );
}
